<template>
  <div class="game-trend">
    <el-card class="chart-card">
      <div slot="header" class="card-header">
        <span>比赛类型趋势</span>
        <div class="operations">
          <!-- 赛事类型选择器 -->
          <div class="filter-item">
            <el-select
              v-model="gameTypes"
              multiple
              collapse-tags
              placeholder="选择比赛类型"
              class="type-select"
              @change="handleTypeChange"
            >
              <div class="select-actions">
                <el-button size="mini" type="text" @click.stop.prevent="handleSelectAll">全选</el-button>
                <el-divider direction="vertical" />
                <el-button size="mini" type="text" @click.stop.prevent="clearTypeFilter">重置</el-button>
              </div>
              <el-option
                v-for="item in gameTypeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>
          
          <!-- 赛事时间选择器 -->
          <div class="filter-item">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              :clearable="true"
              :picker-options="pickerOptions"
              @change="handleDateChange"
            />
          </div>
          
          <div class="button-group">
            <el-tooltip content="重置所有筛选" placement="top">
              <el-button type="text" icon="el-icon-refresh" @click="handleReset" />
            </el-tooltip>
            <el-tooltip content="导出图片" placement="top">
              <el-button type="text" icon="el-icon-download" @click="handleExport" />
            </el-tooltip>
            <el-tooltip content="放大显示" placement="top">
              <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
            </el-tooltip>
          </div>
        </div>
      </div>
      <div class="chart-container" ref="chart"></div>
    </el-card>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="90%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog vertical-center-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
      @close="handleDialogClose"
    >
      <div slot="title" class="dialog-header">
        <span>成绩趋势</span>
        <div class="dialog-operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          </el-tooltip>
          <el-tooltip content="关闭" placement="top">
            <el-button type="text" icon="el-icon-close" @click="closeDialog" />
          </el-tooltip>
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import _ from 'lodash'

export default {
  name: 'GameTrendChart',
  data() {
    return {
      chart: null,
      chartData: {
        dates: [],
        scores: [],
        ranks: [],
        totals: [],
        names: [],
        times: [],
        gameTypes: []
      },
      gameTypes: [],
      gameTypeOptions: [],
      dateRange: null,
      pickerOptions: {
        shortcuts: [
          {
            text: '重置',
            onClick(picker) {
              picker.$emit('pick', null)
            }
          },
          {
            text: '最近一个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近三个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近半年',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 180)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近一年',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 365)
              picker.$emit('pick', [start, end])
            }
          }
        ]
      },
      fullscreenChart: null,
      dialogVisible: false,
      resizeHandler: null,
      isInitializing: false,
      typeColors: {}
    }
  },
  mounted() {
    this.initChart()
    this.fetchGameTypes()
    this.fetchData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
    if (this.fullscreenChart) {
      this.fullscreenChart.dispose()
    }
    window.removeEventListener('resize', this.resizeChart)
  },
  watch: {
    dialogVisible(val) {
      if (!val && this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    }
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.chart)
    },
    resizeChart() {
      this.chart?.resize()
    },
    async fetchData() {
      try {
        console.log('开始获取数据...')
        console.log('当前日期范围:', this.dateRange)
        console.log('当前选中类型:', this.gameTypes)
        
        // 构建基本参数
        const params = {}
        
        // 添加赛事类型参数
        if (this.gameTypes && this.gameTypes.length > 0) {
          params.game_types = this.gameTypes
        }
        
        // 添加日期范围参数
        let startDate = null
        let endDate = null
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_date = this.dateRange[0]
          params.end_date = this.dateRange[1]
          
          // 保存日期对象用于前端过滤
          startDate = new Date(this.dateRange[0])
          endDate = new Date(this.dateRange[1])
          
          console.log('添加日期范围参数:', params.start_date, '-', params.end_date)
        }
        
        console.log('请求参数:', JSON.stringify(params, null, 2))
        
        // 发送请求
        const res = await request.get('/api/analysis/game/trend', { params })
        
        console.log('API Response:', res)
        
        // 获取原始数据
        let rawData = null
        if (res.code === 200 && res.data) {
          rawData = res.data
        } else if (res.data) {
          rawData = res.data
        } else {
          console.error('API返回异常数据:', res)
          this.$message.error('获取数据失败')
          return
        }
        
        // 如果设置了日期范围，在前端进行过滤
        if (startDate && endDate && rawData.dates && rawData.dates.length > 0) {
          console.log('在前端根据日期范围过滤数据:', startDate, '-', endDate)
          
          // 过滤符合日期范围的数据
          const filteredIndices = []
          rawData.dates.forEach((dateStr, index) => {
            const currentDate = new Date(dateStr)
            if (currentDate >= startDate && currentDate <= endDate) {
              filteredIndices.push(index)
            }
          })
          
          console.log('符合日期范围的索引:', filteredIndices)
          
          // 根据过滤后的索引创建新的数据集
          const filteredData = {
            dates: [],
            scores: [],
            ranks: [],
            totals: [],
            names: [],
            times: [],
            game_types: []
          }
          
          filteredIndices.forEach(index => {
            filteredData.dates.push(rawData.dates[index])
            filteredData.scores.push(rawData.scores[index])
            filteredData.ranks.push(rawData.ranks[index])
            filteredData.totals.push(rawData.totals[index])
            filteredData.names.push(rawData.names[index])
            filteredData.times.push(rawData.dates[index]) // 使用dates作为times
            filteredData.game_types.push(rawData.game_types[index])
          })
          
          // 使用过滤后的数据
          this.chartData = {
            dates: filteredData.dates || [],
            scores: filteredData.scores || [],
            ranks: filteredData.ranks || [],
            totals: filteredData.totals || [],
            names: filteredData.names || [],
            times: filteredData.dates || [],
            gameTypes: filteredData.game_types || []
          }
          
          console.log('前端过滤后的数据:', this.chartData)
        } else {
          // 使用原始数据
          this.chartData = {
            dates: rawData.dates || [],
            scores: rawData.scores || [],
            ranks: rawData.ranks || [],
            totals: rawData.totals || [],
            names: rawData.names || [],
            times: rawData.dates || [],
            gameTypes: rawData.game_types || []
          }
        }
        
        this.updateChart()
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败')
      }
    },
    async fetchGameTypes() {
      try {
        const res = await request.get('/api/dict/list', {
          params: {
            dict_key: 'game_type'
          }
        })
        if (res.data && Array.isArray(res.data)) {
          const colors = [
            '#3498DB',  // 天蓝色
            '#E74C3C',  // 鲜红色
            '#2ECC71',  // 翠绿色
            '#9B59B6',  // 紫色
            '#F1C40F',  // 金黄色
            '#1ABC9C',  // 青绿色
            '#E67E22',  // 橙色
            '#34495E',  // 深蓝灰色
            '#C0392B',  // 深红色
            '#27AE60',  // 深绿色
            '#8E44AD',  // 深紫色
            '#F39C12',  // 琥珀色
            '#16A085',  // 深青色
            '#D35400',  // 深橙色
            '#7F8C8D',  // 灰色
            '#2980B9',  // 深天蓝色
            '#BA4A00',  // 赭石色
            '#145A32',  // 森林绿
            '#641E16',  // 暗红色
            '#D98880'   // 玫瑰色
          ]
          
          this.typeColors = {}
          res.data.forEach((item, index) => {
            this.typeColors[item.dict_value] = colors[index % colors.length]
          })

          this.gameTypeOptions = res.data.map(item => ({
            value: item.dict_value,
            label: item.dict_value
          }))
          this.gameTypes = this.gameTypeOptions.map(type => type.value)
        } else {
          console.error('获取赛事类型数据格式错误:', res)
          this.$message.error('获取赛事类型数据格式错误')
        }
      } catch (error) {
        console.error('获取赛事类型失败:', error)
        this.$message.error('获取赛事类型失败')
        this.gameTypeOptions = []
        this.gameTypes = []
      }
    },
    formatRank(rank, total) {
      if (rank === 0 && total === 0) return '未添加'
      if (!rank && rank !== 0) return null
      if (rank < 0.01) return 'Top 0.01%'
      return rank.toFixed(2) + '%'
    },
    formatScore(score) {
      return score.toFixed(2) + '%'
    },
    updateChart() {
      // 如果没有选择任何类型，清空图表
      if (this.gameTypes.length === 0) {
        const emptyOption = {
          backgroundColor: '#ffffff',
          grid: {
            top: 35,
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: []
          },
          yAxis: [
            {
              type: 'value',
              name: '得分率',
              min: 0,
              max: 100,
              position: 'left',
              offset: 0,
              nameLocation: 'end',
              nameGap: 15,
              nameRotate: 0,
              nameTextStyle: {
                align: 'right',
                verticalAlign: 'bottom',
                padding: [0, 20, 0, 0]
              },
              axisLabel: {
                formatter: '{value}%',
                align: 'right',
                padding: [0, 15, 0, 0]
              }
            },
            {
              type: 'value',
              name: '排位 Top',
              min: 0,
              max: 100,
              position: 'right',
              inverse: true,
              offset: 20,
              nameLocation: 'start',
              nameGap: 15,
              nameRotate: 0,
              nameTextStyle: {
                align: 'left',
                verticalAlign: 'bottom',
                padding: [0, 20, 0, 15]
              },
              axisLabel: {
                formatter: '{value}%',
                align: 'center',
                padding: [0, 15, 0, 15]
              }
            }
          ],
          series: []
        }
        this.chart.setOption(emptyOption, true)
        return
      }

      const validScores = this.chartData.scores.filter(score => score !== null && score !== undefined)
      const validRanks = this.chartData.ranks.filter((rank, index) => {
        const total = this.chartData.totals[index]
        return rank !== null && rank !== undefined && !(rank === 0 && total === 0)
      })
      
      const averageScore = validScores.length > 0 
        ? validScores.reduce((sum, score) => sum + score, 0) / validScores.length 
        : 0
      
      const averageRank = validRanks.length > 0
        ? validRanks.reduce((sum, rank) => sum + rank, 0) / validRanks.length
        : 0

      console.log('Updating chart with data:')
      console.log('Game Types:', this.chartData.gameTypes)
      console.log('Type Colors:', this.typeColors)
      console.log('Scores:', this.chartData.scores)
      
      this.chartData.gameTypes.forEach((type, index) => {
        console.log(`Data point ${index}:`, {
          gameType: type,
          color: this.typeColors[type],
          score: this.chartData.scores[index]
        })
      })

      const option = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: function(params) {
            if (!params || !Array.isArray(params) || params.length === 0) return ''
            const index = params[0].dataIndex
            const time = this.chartData.times[index] || ''
            const name = this.chartData.names[index] || ''
            const title = name || time
            const gameType = this.chartData.gameTypes?.[index]
            
            // 获取赛事类型标签样式
            const getGameTypeTagStyle = (type) => {
              if (!type || !this.typeColors[type]) return null
              const color = this.typeColors[type]
              // 将颜色转换为 rgba 格式用于背景
              const hexToRgba = (hex, alpha) => {
                const r = parseInt(hex.slice(1, 3), 16)
                const g = parseInt(hex.slice(3, 5), 16)
                const b = parseInt(hex.slice(5, 7), 16)
                return `rgba(${r}, ${g}, ${b}, ${alpha})`
              }
              const bg = hexToRgba(color, 0.2)
              return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 11px; background-color: ${bg}; border: 1px solid ${color}; color: ${color}; line-height: 1.2;`
            }
            
            let content = ''
            // 如果有赛事类型，显示带颜色的标签
            if (gameType) {
              const tagStyle = getGameTypeTagStyle(gameType)
              if (tagStyle) {
                content += `<div><span style="${tagStyle}">${gameType}</span></div>`
              } else {
                content += `<div>赛事类型：${gameType}</div>`
              }
            }
            if (time && name) {
              content += `<div>时间：${time}</div>`
            }
            params.forEach(param => {
              if (param.seriesName === '得分率' && param.value !== null && param.value !== undefined) {
                content += `<div>${param.seriesName}：${this.formatScore(param.value)}</div>`
              } else if (param.seriesName === '排位') {
                const rank = this.chartData.ranks[index]
                const total = this.chartData.totals[index]
                if (rank === 0 && total === 0) {
                  content += `<div>${param.seriesName}：未添加</div>`
                } else {
                  content += `<div>${param.seriesName}：Top ${rank.toFixed(2)}%</div>`
                  const actualRank = Math.round(rank * total / 100)
                  content += `<div>排名：${actualRank}/${total}</div>`
                }
              }
            })
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${title}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  ${content}
                </div>
              </div>
            `
          }.bind(this)
        },
        grid: {
          top: '8%',
          left: '1%',
          right: '1%',
          bottom: '10%',
          containLabel: true
        },
        legend: {
          data: [
            '得分率',
            {
              name: '平均得分率',
              itemStyle: {
                opacity: 0
              },
              lineStyle: {
                type: 'dashed',
                width: 1,
                color: '#409EFF'
              }
            },
            '排位',
            {
              name: '平均排位',
              itemStyle: {
                opacity: 0
              },
              lineStyle: {
                type: 'dashed',
                width: 1,
                color: '#67C23A'
              }
            }
          ],
          bottom: '0%',
          left: 'center',
          itemGap: 35,
          itemWidth: 20,
          itemHeight: 12,
          textStyle: {
            fontSize: 12,
            color: '#666',
            padding: [0, 4]
          },
          inactiveColor: '#ccc'
        },
        xAxis: {
          type: 'category',
          data: this.chartData.dates,
          axisLabel: {
            show: true,
            interval: (index, value) => {
              const total = this.chartData.dates.length
              if (total <= 5) return true
              
              if (index === 0 || index === total - 1) return true
              
              const step = Math.floor(total / 4)
              return index === step || index === 2 * step || index === 3 * step
            },
            formatter: (value, index) => {
              const date = new Date(value)
              return `${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`
            }
          },
          axisTick: {
            show: true,
            interval: (index, value) => {
              const total = this.chartData.dates.length
              if (total <= 5) return true
              
              if (index === 0 || index === total - 1) return true
              
              const step = Math.floor(total / 4)
              return index === step || index === 2 * step || index === 3 * step
            }
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '得分率',
            min: 0,
            max: 100,
            position: 'left',
            offset: 0,
            nameLocation: 'start',
            nameGap: 35,
            nameRotate: 0,
            nameTextStyle: {
              align: 'right',
              verticalAlign: 'top',
              padding: [0, 15, 0, 0],
              color: '#666',
              fontSize: 12
            },
            axisLabel: {
              formatter: '{value}%',
              align: 'right',
              padding: [0, 15, 0, 0],
              color: '#666'
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
                color: '#E4E7ED'
              }
            },
            axisLine: {
              show: false
            }
          },
          {
            type: 'value',
            name: '排位 Top',
            min: 0,
            max: 100,
            position: 'right',
            inverse: true,
            offset: 0,
            nameLocation: 'end',
            nameGap: 40,
            nameRotate: 0,
            nameTextStyle: {
              align: 'left',
              verticalAlign: 'bottom',
              padding: [0, 0, 0, 15],
              color: '#666',
              fontSize: 12
            },
            axisLabel: {
              formatter: (value) => {
                const index = this.chartData.ranks.indexOf(value)
                if (value === 0) {
                  return '0.00%/未添加'
                }
                return this.formatRank(value, this.chartData.totals[index]) || ''
              },
              align: 'left',
              padding: [0, 0, 0, 15],
              color: '#666'
            },
            splitLine: {
              show: false
            },
            axisLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: '得分率',
            type: 'bar',
            data: this.chartData.scores.map((score, index) => {
              const gameType = this.chartData.gameTypes[index]
              const color = this.typeColors[gameType]
              return {
                value: score,
                itemStyle: {
                  color: color || '#409EFF',
                  borderRadius: [3, 3, 0, 0]
                }
              }
            }),
            yAxisIndex: 0,
            barMaxWidth: 35,
            barGap: '20%'
          },
          {
            name: '排位',
            type: 'line',
            smooth: true,
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: {
              color: '#5470c6',
              borderWidth: 2
            },
            lineStyle: {
              width: 2,
              color: '#5470c6',
              type: 'solid'
            },
            data: this.chartData.ranks.map((rank, index) => {
              const total = this.chartData.totals[index]
              const isSpecial = rank === 0 && total === 0
              const actualRank = Math.round(rank * total / 100)
              
              return {
                value: actualRank === 1 ? 0 : rank,
                itemStyle: {
                  color: actualRank === 1 ? '#67C23A' : (isSpecial ? '#ff4d4f' : '#5470c6')
                }
              }
            }),
            yAxisIndex: 1,
            connectNulls: true
          },
          {
            name: '平均得分率',
            type: 'line',
            smooth: true,
            showSymbol: false,
            symbol: 'none',
            lineStyle: {
              type: 'dashed',
              width: 1,
              color: '#409EFF'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{
                offset: 0,
                color: 'rgba(64, 158, 255, 0.3)'
              }, {
                offset: 1,
                color: 'rgba(64, 158, 255, 0)'
              }])
            },
            data: new Array(this.chartData.dates.length).fill(averageScore)
          },
          {
            name: '平均排位',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            showSymbol: false,
            symbol: 'none',
            lineStyle: {
              type: 'dashed',
              width: 1,
              color: '#67C23A'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 1, 0, 0, [{
                offset: 0,
                color: 'rgba(103, 194, 58, 0.3)'
              }, {
                offset: 1,
                color: 'rgba(103, 194, 58, 0)'
              }])
            },
            data: new Array(this.chartData.dates.length).fill(averageRank)
          }
        ]
      }

      console.log('Final chart option:', option)
      this.chart.setOption(option, true)
    },
    handleExport() {
      const dataURL = this.chart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })
      
      const link = document.createElement('a')
      link.download = '成绩趋势.png'
      link.href = dataURL
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    handleDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.fullscreenChart
          if (!chartDom) return

          this.fullscreenChart = echarts.init(chartDom)
          const option = this.chart.getOption()
          
          option.grid = {
            left: '5%',
            right: '5%',
            bottom: '10%',
            top: '10%',
            containLabel: true
          }

          this.fullscreenChart.setOption(option)
          
          this.resizeHandler = _.debounce(() => {
            if (this.fullscreenChart) {
              this.fullscreenChart.resize()
            }
          }, 100)

          window.addEventListener('resize', this.resizeHandler)
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleDialogClose() {
      this.cleanupFullscreen()
    },
    handleDialogClosed() {
      this.isInitializing = false
    },
    cleanupFullscreen() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }

      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    handleFullscreen() {
      this.cleanupFullscreen()
      this.dialogVisible = true
    },
    closeDialog() {
      this.dialogVisible = false
    },
    handleExportFullscreen() {
      if (!this.fullscreenChart) return
      
      try {
        const dataURL = this.fullscreenChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff'
        })
        
        const link = document.createElement('a')
        link.download = '成绩趋势.png'
        link.href = dataURL
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    handleTypeChange() {
      console.log('类型选择变化:', this.gameTypes)
      // 保持现有的dateRange不变，只更新类型筛选
      this.fetchData()
    },
    
    // 处理日期变化
    handleDateChange(val) {
      console.log('日期范围变化:', val)
      this.dateRange = val
      this.fetchData()
    },
    
    handleSelectAll() {
      this.gameTypes = this.gameTypeOptions.map(type => type.value)
      // 保持现有的dateRange不变
      this.fetchData()
    },
    
    handleReset() {
      // 重置所有筛选条件：类型全选，时间清除
      this.gameTypes = this.gameTypeOptions.map(type => type.value)
      this.dateRange = null
      this.fetchData()
    },
    
    // 清除单个筛选条件
    clearDateFilter() {
      this.dateRange = null
      this.fetchData()
    },
    
    clearTypeFilter() {
      this.gameTypes = []
      this.fetchData()
    }
  }
}
</script>

<style scoped>
.game-trend {
  width: 100%;
  box-sizing: border-box;
}

.chart-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  height: 100%;
}

.chart-card :deep(.el-card__header) {
  padding: 0;
  border-bottom: none;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}

.chart-container {
  width: 100%;
  height: 400px;
  padding: 20px;
  box-sizing: border-box;
}

.operations {
  display: flex;
  gap: 12px;
  align-items: center;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.button-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.operations .el-button {
  color: #ffffff;
}

.operations .el-button:hover {
  color: #BD6C73;
}

.type-select {
  width: 200px;
}

/* 分开定义两个控件的样式，不使用组合选择器 */
.type-select :deep(.el-input__inner) {
  height: 28px;
  line-height: 28px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: #fff;
  padding-right: 25px;
}

.type-select :deep(.el-input__inner:hover) {
  background-color: rgba(255, 255, 255, 0.2);
}

.type-select :deep(.el-input__suffix) {
  right: 5px;
}

.type-select :deep(.el-select__caret) {
  color: #fff;
  line-height: 28px;
}

/* 单独为日期控件设置样式 */
.filter-item :deep(.el-date-editor.el-input),
.filter-item :deep(.el-date-editor.el-range-editor) {
  width: 260px !important;
  height: 28px !important;
  line-height: 28px !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
  border: none !important;
  border-radius: 4px !important;
}

.filter-item :deep(.el-date-editor.el-range-editor:hover) {
  background-color: rgba(255, 255, 255, 0.2) !important;
}

.filter-item :deep(.el-range-input) {
  background-color: transparent !important;
  color: #fff !important;
  font-size: 12px !important;
  height: 100% !important;
  margin: 0 !important;
  vertical-align: middle !important;
  width: 90px !important;
  padding: 0 2px !important;
}

.filter-item :deep(.el-range-separator) {
  color: #fff !important;
  padding: 0 3px !important;
  line-height: 22px !important;
}

.filter-item :deep(.el-range__icon),
.filter-item :deep(.el-range__close-icon),
.filter-item :deep(.el-input__icon) {
  color: #fff !important;
  line-height: 28px !important;
  font-size: 14px !important;
  margin-top: -1px !important;
}

.filter-item :deep(.el-date-editor.el-range-editor) {
  padding: 0 8px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
}

/* 对象样式覆盖以解决外部容器问题 */
.filter-item :deep(.el-range-editor.el-input__inner) {
  height: 28px !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
  border: none !important;
}

/* 解决输入框可能的显示问题 */
.filter-item :deep(.el-range-editor .el-range-input::-webkit-input-placeholder) {
  color: rgba(255, 255, 255, 0.7) !important;
}
.filter-item :deep(.el-range-editor .el-range-input::-moz-placeholder) {
  color: rgba(255, 255, 255, 0.7) !important;
}
.filter-item :deep(.el-range-editor .el-range-input:-ms-input-placeholder) {
  color: rgba(255, 255, 255, 0.7) !important;
}

/* 保留后面的样式 */
.type-select :deep(.el-select__tags) {
  background-color: transparent;
  height: 28px;
  line-height: 28px;
  padding: 0 4px;
}

.type-select :deep(.el-tag) {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: transparent;
  color: #fff;
  height: 20px;
  line-height: 20px;
  margin: 4px 0;
  border-radius: 2px;
}

.type-select :deep(.el-tag__close) {
  color: #fff;
  background-color: transparent;
}

.type-select :deep(.el-tag__close:hover) {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.3);
}

.type-select :deep(.el-select__input) {
  color: #fff;
  margin-left: 0;
}

.type-select :deep(.el-select-dropdown__item) {
  height: 34px;
  line-height: 34px;
}

.type-select :deep(.el-select-dropdown__item.selected) {
  color: #409EFF;
  font-weight: bold;
}

.type-select :deep(.el-select-dropdown__item.hover) {
  background-color: rgba(64, 158, 255, 0.1);
}

.select-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-bottom: 1px solid #EBEEF5;
  width: 100%;
  height: 34px;
  line-height: 34px;
}

.select-actions .el-button {
  font-size: 14px;
  padding: 0;
  height: 34px;
  line-height: 34px;
  flex: 1;
  text-align: center;
}

.select-actions .el-divider--vertical {
  height: 14px;
  margin: 0 2px;
}

.type-select :deep(.el-select-dropdown__wrap) {
  margin-bottom: 0;
  max-height: 240px;
}

.type-select :deep(.el-select-dropdown__item) {
  height: 34px;
  line-height: 34px;
  font-size: 14px;
}

.type-select :deep(.el-divider--horizontal) {
  margin: 1px 0;
}

.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-operations {
  display: flex;
  gap: 8px;
}

.dialog-operations .el-button {
  color: #ffffff;
}

.dialog-operations .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 600px;
  padding: 20px;
  box-sizing: border-box;
}
</style> 